import type { PartnerBranchResponse } from '@palaccount/contracts';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Gated } from '@/components/permission-gate';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Ltr } from '@/components/amount';
import { localizeServerMessage } from '@/lib/server-messages';

import { usePartnerBranchMutations, usePartnerBranches } from './api';

interface FormState {
  name: string;
  address: string;
  city: string;
  phone: string;
  contactName: string;
}

const EMPTY: FormState = { name: '', address: '', city: '', phone: '', contactName: '' };

/**
 * INV-F3 — the partner's BRANCHES card: Name | Address | City | Phone |
 * Contact | status, with add/edit in one small form and switch off/on
 * instead of delete (the manufacturers page's shape, on a partner).
 *
 * Saved THROUGH THE BRANCHES ENDPOINTS, not with the partner form: a branch
 * belongs to a partner that exists, so a NEW partner's card says "save
 * first" and nothing else — there is no id to hang a branch on yet.
 *
 * `readOnly` is the detail page: the list, no controls.
 */
export function PartnerBranchesCard({
  partnerId,
  readOnly = false,
}: {
  partnerId: string | undefined;
  readOnly?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('partners.secBranches')}</CardTitle>
      </CardHeader>
      <CardContent className="grid gap-4">
        {partnerId === undefined ? (
          <p className="text-sm text-muted-foreground">{t('partners.branchesSaveFirst')}</p>
        ) : (
          <BranchesBody partnerId={partnerId} readOnly={readOnly} />
        )}
      </CardContent>
    </Card>
  );
}

function BranchesBody({ partnerId, readOnly }: { partnerId: string; readOnly: boolean }) {
  const { t } = useTranslation();
  const [showInactive, setShowInactive] = useState(false);
  const [editing, setEditing] = useState<PartnerBranchResponse | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [values, setValues] = useState<FormState>(EMPTY);
  const [error, setError] = useState<string | null>(null);

  const { data: branches, isPending } = usePartnerBranches(partnerId, !readOnly && showInactive);
  const { create, update, setActive } = usePartnerBranchMutations(partnerId);

  const fail = (cause: unknown) =>
    setError(
      localizeServerMessage(t, cause instanceof Error ? cause.message : String(cause), cause),
    );

  const openNew = () => {
    setEditing(null);
    setValues(EMPTY);
    setError(null);
    setFormOpen(true);
  };
  const openEdit = (branch: PartnerBranchResponse) => {
    setEditing(branch);
    setValues({
      name: branch.name,
      address: branch.address ?? '',
      city: branch.city ?? '',
      phone: branch.phone ?? '',
      contactName: branch.contactName ?? '',
    });
    setError(null);
    setFormOpen(true);
  };

  const submit = async () => {
    setError(null);
    const name = values.name.trim();
    if (name === '') return setError(t('partners.errBranchName'));
    const text = (value: string) => (value.trim() === '' ? undefined : value.trim());
    try {
      if (editing) {
        // An emptied box CLEARS the field on edit (null); on create it is
        // simply not sent — the manufacturers card's convention.
        const cleared = (value: string) => (value.trim() === '' ? null : value.trim());
        await update.mutateAsync({
          branchId: editing.id,
          body: {
            name,
            address: cleared(values.address),
            city: cleared(values.city),
            phone: cleared(values.phone),
            contactName: cleared(values.contactName),
          },
        });
      } else {
        await create.mutateAsync({
          name,
          address: text(values.address),
          city: text(values.city),
          phone: text(values.phone),
          contactName: text(values.contactName),
        });
      }
      setFormOpen(false);
    } catch (cause) {
      fail(cause);
    }
  };

  const rows = branches ?? [];

  return (
    <>
      {!readOnly ? (
        <div className="flex flex-wrap items-center gap-3">
          <Label className="flex items-center gap-2 font-normal">
            <Checkbox
              checked={showInactive}
              onCheckedChange={(checked) => setShowInactive(checked === true)}
            />
            {t('common.showInactive')}
          </Label>
          <Gated permission="crm.edit" reasonKey="perm.cannot.editPartners" className="ms-auto">
            <Button type="button" variant="outline" onClick={openNew}>
              <Plus aria-hidden /> {t('partners.branchAdd')}
            </Button>
          </Gated>
        </div>
      ) : null}

      {formOpen && !readOnly ? (
        <div className="grid gap-4 rounded-[11px] border border-input p-4">
          <h3 className="text-sm font-semibold">
            {t(editing ? 'partners.branchEdit' : 'partners.branchNew')}
          </h3>
          {error ? (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          ) : null}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-1.5">
              <Label htmlFor="pb-name">{t('partners.fBranchName')}</Label>
              <Input
                id="pb-name"
                maxLength={120}
                value={values.name}
                onChange={(event) => setValues({ ...values, name: event.target.value })}
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="pb-address">{t('partners.fBranchAddress')}</Label>
              <Input
                id="pb-address"
                maxLength={200}
                value={values.address}
                onChange={(event) => setValues({ ...values, address: event.target.value })}
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="pb-city">{t('partners.fCity')}</Label>
              <Input
                id="pb-city"
                maxLength={60}
                value={values.city}
                onChange={(event) => setValues({ ...values, city: event.target.value })}
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="pb-phone">{t('partners.fPhone')}</Label>
              {/* MD-01 field 7's rule, on the branch: a phone reads left-to-right. */}
              <Input
                id="pb-phone"
                dir="ltr"
                maxLength={30}
                value={values.phone}
                onChange={(event) => setValues({ ...values, phone: event.target.value })}
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="pb-contact">{t('partners.fBranchContact')}</Label>
              <Input
                id="pb-contact"
                maxLength={120}
                value={values.contactName}
                onChange={(event) => setValues({ ...values, contactName: event.target.value })}
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Gated permission="crm.edit" reasonKey="perm.cannot.editPartners">
              <Button type="button" onClick={() => void submit()} disabled={!values.name.trim()}>
                {t('common.save')}
              </Button>
            </Gated>
            <Button type="button" variant="outline" onClick={() => setFormOpen(false)}>
              {t('common.cancel')}
            </Button>
            {editing ? (
              <Gated permission="crm.edit" reasonKey="perm.cannot.editPartners">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setError(null);
                    setActive
                      .mutateAsync({ branchId: editing.id, active: !editing.isActive })
                      .then(() => setFormOpen(false))
                      .catch(fail);
                  }}
                >
                  {t(editing.isActive ? 'items.switchOff' : 'items.switchOn')}
                </Button>
              </Gated>
            ) : null}
          </div>
        </div>
      ) : null}

      {isPending ? (
        <div className="h-16 animate-pulse rounded-md bg-muted" aria-busy />
      ) : rows.length === 0 ? (
        <p className="py-4 text-center text-sm text-muted-foreground">{t('partners.branchesEmpty')}</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('partners.fBranchName')}</TableHead>
              <TableHead>{t('partners.fBranchAddress')}</TableHead>
              <TableHead>{t('partners.fCity')}</TableHead>
              <TableHead>{t('partners.fPhone')}</TableHead>
              <TableHead>{t('partners.fBranchContact')}</TableHead>
              <TableHead>{t('items.colStatus')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((branch) => (
              <TableRow key={branch.id}>
                <TableCell>
                  {readOnly ? (
                    <span className="font-medium">{branch.name}</span>
                  ) : (
                    // The name opens the editor — the manufacturers list's convention.
                    <button
                      type="button"
                      className="text-start font-medium hover:underline"
                      aria-label={t('partners.branchEditNamed', { name: branch.name })}
                      onClick={() => openEdit(branch)}
                    >
                      {branch.name}
                    </button>
                  )}
                </TableCell>
                <TableCell>{branch.address ?? '—'}</TableCell>
                <TableCell>{branch.city ?? '—'}</TableCell>
                <TableCell>{branch.phone ? <Ltr value={branch.phone} /> : '—'}</TableCell>
                <TableCell>{branch.contactName ?? '—'}</TableCell>
                <TableCell>
                  <span className="text-xs text-muted-foreground">
                    {t(branch.isActive ? 'common.active' : 'common.inactive')}
                  </span>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </>
  );
}
